import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Dialog,
  DialogContent,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  InputAdornment,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import * as BrokerStyles from "../Broker/Styles.js";
import {
  buildTableRowsPerPageOptions,
  TABLE_COMPACT_HEADER_PADDING,
  TABLE_INITIAL_ROWS_PER_PAGE,
} from "../Table/constants.js";

import {
  PageRoot,
  TitleWrap,
  MainCard__body,
  Card,
  HeaderCell,
  BodyCell,
  Footer,
  TotalText,
  Toolbar,
} from "./Styles";

import {
  getEntities,
  createEntity,
  updateEntity,
  deleteEntity,
} from "../../Endpoints/SettingEntities";

const SERVER_OPTIONS = [
  "MT4 LTD Demo",
  "MT5 LTD Demo",
  "MT4 EU Demo",
  "MT5 EU Demo",
  "MT5 EU Live",
  "MT4 EU Live",
  "MT5 LTD Test",
  "MT4 LTD Live",
  "MT5 LTD Live",
];

const ROWS_OPTIONS = buildTableRowsPerPageOptions([5, 10, 25, 50, 100]);

const ENTITY_COLUMNS = [
  { field: "name", label: "Name", minWidth: 180, width: 210 },
  { field: "address", label: "Address", minWidth: 240, width: 300 },
  { field: "initials", label: "Entity initials", minWidth: 140, width: 170 },
  {
    field: "actions",
    label: "Actions",
    minWidth: 110,
    width: 120,
    align: "right",
    resizable: false,
  },
];

const initialEntityColumnWidths = ENTITY_COLUMNS.reduce((widths, column) => {
  widths[column.field] = column.width;
  return widths;
}, {});

const emptyForm = {
  name: "",
  initials: "",
  entityAddress: "",
  value: "",
  server: "",
};

function CompanyEntities() {
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(TABLE_INITIAL_ROWS_PER_PAGE);
  const [search, setSearch] = useState("");
  const [columnWidths, setColumnWidths] = useState(initialEntityColumnWidths);

  const [entities, setEntities] = useState([]);
  const [apiMeta, setApiMeta] = useState({
    email: "",
    role: "",
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [openCreate, setOpenCreate] = useState(false);
  const [createForm, setCreateForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  const [openEdit, setOpenEdit] = useState(false);
  const [editForm, setEditForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState("");

  const [deletingId, setDeletingId] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedDeleteEntity, setSelectedDeleteEntity] = useState(null);

  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [successTitle, setSuccessTitle] = useState("Entity saved successfully");
  const [successDescription, setSuccessDescription] = useState(
    "The entity has been saved successfully and is now available in the system."
  );
  const [successEntityId, setSuccessEntityId] = useState("");

  const chips = useMemo(
    () => [
      { key: "s1", label: "#MetaTrader 5 Account" },
      { key: "s2", label: "#MT4 LTD DEMO" },
      { key: "s3", label: "#MetaTrader 5 Account" },
    ],
    []
  );

  const [visibleCreateChips, setVisibleCreateChips] = useState(
    chips.map((c) => c.key)
  );
  const [visibleEditChips, setVisibleEditChips] = useState(
    chips.map((c) => c.key)
  );

  const chipSx = {
    borderRadius: 999,
    backgroundColor: "#FEEFC7",
    color: "#5F4B00",
    "& .MuiChip-deleteIcon": {
      color: "#5F4B00",
      "&:hover": { color: "#000" },
    },
  };

  const centeredChipsSx = {
    display: "flex",
    flexWrap: "wrap",
    gap: 1,
    pt: 0.5,
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
  };

  const arrowBtnSx = (disabled) => ({
    minWidth: 30,
    height: 30,
    borderRadius: "10px",
    backgroundColor: "#FFFFFF",
    border: "1px solid #E5E7EB",
    color: disabled ? "#9CA3AF" : "#374151",
    boxShadow: "none",
    padding: 0,
    "&:hover": {
      backgroundColor: "#F9FAFB",
      borderColor: "#D1D5DB",
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      backgroundColor: "#FFFFFF",
      borderColor: "#E5E7EB",
      color: "#9CA3AF",
      opacity: 1,
    },
  });

  const handleDeleteCreateChip = (key) => {
    setVisibleCreateChips((prev) => prev.filter((k) => k !== key));
  };

  const handleDeleteEditChip = (key) => {
    setVisibleEditChips((prev) => prev.filter((k) => k !== key));
  };

  const openSuccess = ({ title, description, id }) => {
    setSuccessTitle(title);
    setSuccessDescription(description);
    setSuccessEntityId(id || "");
    setOpenSuccessModal(true);
  };

  const fetchEntities = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await getEntities();
      if (!response) {
        setError("Failed to load entities.");
        setEntities([]);
        return;
      }
      const payload = response?.data?.data ?? response?.data ?? {};

      setApiMeta({
        email: payload.email || "",
        role: payload.role || "",
      });

      const normalizedEntities = Array.isArray(payload.entities)
        ? payload.entities.map((entity) => ({
            id: entity.id,
            name: entity.name || "",
            address: entity.address || "",
            initials: entity.initials || "",
            entities_splits: Array.isArray(entity.entities_splits)
              ? entity.entities_splits
              : [],
          }))
        : [];

      setEntities(normalizedEntities);
    } catch (err) {
      setError(err.message || "Failed to load entities.");
      setEntities([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const tableMinWidth = useMemo(() => {
    return ENTITY_COLUMNS.reduce(
      (total, column) => total + (columnWidths[column.field] || column.width),
      0
    );
  }, [columnWidths]);

  const columnCellSx = useMemo(() => {
    return ENTITY_COLUMNS.reduce((styles, column) => {
      const width = columnWidths[column.field] || column.width;

      styles[column.field] = {
        width,
        minWidth: column.minWidth,
        maxWidth: width,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      };

      return styles;
    }, {});
  }, [columnWidths]);

  const handleColumnResizeStart = useCallback(
    (event, column) => {
      if (column.resizable === false) return;

      event.preventDefault();
      event.stopPropagation();

      const startX = event.clientX;
      const startWidth = columnWidths[column.field] || column.width;
      const previousCursor = document.body.style.cursor;
      const previousUserSelect = document.body.style.userSelect;

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      const handlePointerMove = (moveEvent) => {
        const nextWidth = Math.max(
          column.minWidth,
          startWidth + moveEvent.clientX - startX
        );

        setColumnWidths((prev) => ({
          ...prev,
          [column.field]: nextWidth,
        }));
      };

      const handlePointerUp = () => {
        document.body.style.cursor = previousCursor;
        document.body.style.userSelect = previousUserSelect;
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerUp);
        window.removeEventListener("pointercancel", handlePointerUp);
      };

      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerUp);
      window.addEventListener("pointercancel", handlePointerUp);
    },
    [columnWidths]
  );

  const handleColumnResizeKeyDown = useCallback((event, column) => {
    if (column.resizable === false) return;

    const direction =
      event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;

    if (!direction) return;

    event.preventDefault();
    event.stopPropagation();

    setColumnWidths((prev) => {
      const currentWidth = prev[column.field] || column.width;

      return {
        ...prev,
        [column.field]: Math.max(column.minWidth, currentWidth + direction * 16),
      };
    });
  }, []);

  useEffect(() => {
    fetchEntities();
  }, [fetchEntities]);

  const filteredEntities = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return entities;

    return entities.filter((entity) => {
      const name = String(entity.name ?? "").toLowerCase();
      const address = String(entity.address ?? "").toLowerCase();
      const initials = String(entity.initials ?? "").toLowerCase();

      return name.includes(q) || address.includes(q) || initials.includes(q);
    });
  }, [entities, search]);

  const totalCount = filteredEntities.length;

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(totalCount / rowsPerPage));
  }, [totalCount, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [totalCount, search]);

  useEffect(() => {
    if (page > totalPages - 1) setPage(totalPages - 1);
  }, [page, totalPages]);

  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredEntities.slice(start, start + rowsPerPage);
  }, [filteredEntities, page, rowsPerPage]);

  const pageItems = useMemo(() => {
    const last = totalPages;
    const current = page + 1;

    if (last <= 4) return Array.from({ length: last }, (_, i) => i + 1);
    if (current <= 2) return [1, 2, 3, 4, "…"];
    if (current >= last - 1) return ["…", last - 3, last - 2, last - 1, last];
    return ["…", current - 1, current, current + 1, "…"];
  }, [page, totalPages]);

  const openCreateModal = () => {
    setCreateForm(emptyForm);
    setCreateError("");
    setVisibleCreateChips(chips.map((c) => c.key));
    setOpenCreate(true);
  };

  const closeCreateModal = () => {
    if (creating) return;
    setOpenCreate(false);
    setCreateError("");
  };

  const handleCreateChange = (field) => (e) => {
    setCreateForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleCreate = async () => {
    setCreateError("");
    setCreating(true);

    try {
      const payload = {
        name: createForm.name.trim(),
        initials: createForm.initials.trim(),
        address: createForm.entityAddress.trim(),
        entities_splits: [
          {
            server: createForm.server,
            value: createForm.value.trim(),
          },
        ],
      };

      const response = await createEntity(payload);
      if (!response) {
        setCreateError("Failed to create entity.");
        return;
      }

      await fetchEntities();

      const createdId = response?.data?.data?.id ?? response?.data?.id ?? "";

      setCreateForm(emptyForm);
      setOpenCreate(false);
      setPage(0);

      openSuccess({
        title: "Entity created successfully",
        description:
          "The company entity has been created successfully and is now available in the system.",
        id: createdId,
      });
    } catch (err) {
      setCreateError(err.message || "Failed to create entity.");
    } finally {
      setCreating(false);
    }
  };

  const canCreate =
    createForm.name.trim() &&
    createForm.initials.trim() &&
    createForm.entityAddress.trim() &&
    createForm.value.trim() &&
    createForm.server;

  const openEditModal = (entity) => {
    setEditingId(entity.id);
    setUpdateError("");

    setEditForm({
      name: entity.name || "",
      initials: entity.initials || "",
      entityAddress: entity.address || "",
      value: entity.entities_splits?.[0]?.value || "",
      server: entity.entities_splits?.[0]?.server || "",
    });

    setVisibleEditChips(chips.map((c) => c.key));
    setOpenEdit(true);
  };

  const closeEditModal = () => {
    if (updating) return;
    setOpenEdit(false);
    setEditingId(null);
    setUpdateError("");
  };

  const handleEditChange = (field) => (e) => {
    setEditForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    if (!editingId) return;

    setUpdateError("");
    setUpdating(true);

    try {
      const payload = {
        id: editingId,
        name: editForm.name.trim(),
        initials: editForm.initials.trim(),
        address: editForm.entityAddress.trim(),
        entities_splits: editForm.server
          ? [
              {
                server: editForm.server,
                value: editForm.value.trim(),
              },
            ]
          : [],
      };

      const response = await updateEntity(payload);
      if (!response) {
        setUpdateError("Failed to update entity.");
        return;
      }

      await fetchEntities();
      closeEditModal();

      openSuccess({
        title: "Entity updated successfully",
        description:
          "The company entity has been updated successfully and the changes are now available in the system.",
        id: editingId,
      });
    } catch (err) {
      setUpdateError(err.message || "Failed to update entity.");
    } finally {
      setUpdating(false);
    }
  };

  const canSave =
    editForm.name.trim() &&
    editForm.initials.trim() &&
    editForm.entityAddress.trim() &&
    editForm.value.trim() &&
    editForm.server;

  const openDeleteConfirm = (entity) => {
    setDeleteError("");
    setSelectedDeleteEntity(entity);
    setOpenDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (deletingId) return;
    setOpenDeleteModal(false);
    setSelectedDeleteEntity(null);
    setDeleteError("");
  };

  const handleDelete = async () => {
    if (!selectedDeleteEntity?.id) return;

    setDeleteError("");
    setDeletingId(selectedDeleteEntity.id);

    try {
      const response = await deleteEntity(selectedDeleteEntity.id);
      if (!response) {
        setDeleteError("Failed to delete entity.");
        return;
      }

      await fetchEntities();
      closeDeleteModal();
    } catch (err) {
      setDeleteError(err.message || "Failed to delete entity.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <PageRoot>
      <TitleWrap sx={{ px: 4, pt: 2, pb: 3 }}>
        <Button
          startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 16 }} />}
          onClick={() => navigate(-1)}
          sx={{ textTransform: "none", mb: 1.5, color: "#111827" }}
        >
          Go back
        </Button>

        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
        >
          <Box>
            <Typography variant="h4" fontWeight={800} sx={{ mb: 0.5 }}>
              Company entities
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Browse the list or add new entities as needed.
            </Typography>

          </Box>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              borderRadius: 3,
              textTransform: "none",
              fontWeight: 800,
              backgroundColor: "#EB0000",
              "&:hover": {
                backgroundColor: "#D50000",
              },
            }}
            onClick={openCreateModal}
          >
            Create new entity
          </Button>
        </Box>
      </TitleWrap>

      <MainCard__body sx={{ px: 4, pt: 0, pb: 4 }}>
        <Card>
          <Toolbar sx={{ justifyContent: "space-between" }}>
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              sx={{
                ...BrokerStyles.InputStyles,
                width: 320,
                "& .MuiOutlinedInput-root": { borderRadius: "10px" },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Toolbar>

          {loading ? (
            <Box
              sx={{
                py: 8,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: 2,
              }}
            >
              <CircularProgress size={32} />
              <Typography variant="body1" fontWeight={600} color="text.secondary">
                Loading...
              </Typography>
            </Box>
          ) : error ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          ) : (
            <>
              <TableContainer
                sx={{
                  borderRadius: "12px",
                  border: "1px solid rgba(0,0,0,0.06)",
                  overflowX: "auto",
                  background: "#fff",
                  mt: 0.25,
                }}
              >
                <Table sx={{ minWidth: tableMinWidth, tableLayout: "fixed" }}>
                  <colgroup>
                    {ENTITY_COLUMNS.map((column) => (
                      <col
                        key={column.field}
                        style={{
                          width: `${columnWidths[column.field] || column.width}px`,
                        }}
                      />
                    ))}
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      {ENTITY_COLUMNS.map((column) => (
                        <HeaderCell
                          key={column.field}
                          align={column.align || "left"}
                          sx={{
                            ...columnCellSx[column.field],
                            position: "relative",
                            pr:
                              column.resizable === false
                                ? 2
                                : TABLE_COMPACT_HEADER_PADDING,
                            userSelect: "none",
                          }}
                        >
                          <Box
                            component="span"
                            title={column.label}
                            sx={{
                              display: "block",
                              lineHeight: "18px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {column.label}
                          </Box>
                          {column.resizable !== false && (
                            <Box
                              component="span"
                              role="separator"
                              aria-label={`Resize ${column.label} column`}
                              aria-orientation="vertical"
                              aria-valuemin={column.minWidth}
                              aria-valuenow={columnWidths[column.field] || column.width}
                              tabIndex={0}
                              onPointerDown={(event) =>
                                handleColumnResizeStart(event, column)
                              }
                              onKeyDown={(event) =>
                                handleColumnResizeKeyDown(event, column)
                              }
                              sx={{
                                position: "absolute",
                                top: 0,
                                right: 0,
                                width: "12px",
                                height: "100%",
                                cursor: "col-resize",
                                touchAction: "none",
                                "&::after": {
                                  content: '""',
                                  position: "absolute",
                                  top: "22%",
                                  bottom: "22%",
                                  left: "50%",
                                  width: "2px",
                                  transform: "translateX(-50%)",
                                  borderRadius: "2px",
                                  backgroundColor: "rgba(21, 32, 42, 0.22)",
                                },
                                "&:hover::after, &:focus-visible::after": {
                                  backgroundColor: "#15202A",
                                },
                                "&:focus-visible": {
                                  outline: "2px solid #15202A",
                                  outlineOffset: "-2px",
                                },
                              }}
                            />
                          )}
                        </HeaderCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {pagedRows.map((e) => (
                      <TableRow key={e.id} hover>
                        <BodyCell title={e.name} sx={columnCellSx.name}>
                          {e.name}
                        </BodyCell>
                        <BodyCell title={e.address} sx={columnCellSx.address}>
                          {e.address}
                        </BodyCell>
                        <BodyCell title={e.initials} sx={columnCellSx.initials}>
                          {e.initials}
                        </BodyCell>

                        <BodyCell align="right" sx={columnCellSx.actions}>
                          <Stack
                            direction="row"
                            justifyContent="flex-end"
                            spacing={1}
                          >
                            <IconButton
                              size="small"
                              onClick={() => openDeleteConfirm(e)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>

                            <IconButton
                              size="small"
                              onClick={() => openEditModal(e)}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </BodyCell>
                      </TableRow>
                    ))}

                    {pagedRows.length === 0 && (
                      <TableRow>
                        <BodyCell colSpan={4} align="center">
                          No entities found.
                        </BodyCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Footer
                sx={{
                  mt: 1,
                  px: 1.5,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: 1.5,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 400 }}>
                  Total: <b style={{ color: "#111827", fontWeight: 600 }}>{totalCount}</b>
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.2,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                    Pages:&nbsp; <b style={{ color: "#111827" }}>{totalPages}</b>
                  </Typography>

                  <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                    Rows:
                  </Typography>

                  <Select
                    size="small"
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value));
                      setPage(0);
                    }}
                    sx={{
                      height: 32,
                      minWidth: 70,
                      borderRadius: 2,
                      ".MuiSelect-select": { py: 0.5 },
                    }}
                    MenuProps={{
                      PaperProps: { sx: { borderRadius: 2, mt: 0.5 } },
                    }}
                  >
                    {ROWS_OPTIONS.map((n) => (
                      <MenuItem key={n} value={n}>
                        {n}
                      </MenuItem>
                    ))}
                  </Select>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Button
                      size="small"
                      disabled={page === 0}
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      sx={arrowBtnSx(page === 0)}
                    >
                      <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
                    </Button>

                    {pageItems.map((it, idx) => {
                      if (it === "…") {
                        return (
                          <Typography
                            key={`dots-${idx}`}
                            sx={{ fontSize: 14, color: "#9CA3AF", px: 0.5 }}
                          >
                            …
                          </Typography>
                        );
                      }

                      const pageNumber = it;
                      const isActive = pageNumber === page + 1;

                      return (
                        <Button
                          key={`p-${pageNumber}`}
                          size="small"
                          onClick={() => setPage(pageNumber - 1)}
                          sx={{
                            minWidth: 28,
                            height: 28,
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: isActive ? 900 : 700,
                            color: isActive ? "#111827" : "#6B7280",
                            backgroundColor: isActive ? "#EEF2FF" : "transparent",
                            "&:hover": {
                              backgroundColor: isActive ? "#EEF2FF" : "#F3F4F6",
                            },
                          }}
                        >
                          {pageNumber}
                        </Button>
                      );
                    })}

                    <Button
                      size="small"
                      disabled={page >= totalPages - 1}
                      onClick={() =>
                        setPage((p) => Math.min(totalPages - 1, p + 1))
                      }
                      sx={arrowBtnSx(page >= totalPages - 1)}
                    >
                      <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
                    </Button>
                  </Box>
                </Box>
              </Footer>
            </>
          )}
        </Card>
      </MainCard__body>

      {/* CREATE MODAL */}
      <Dialog
        open={openCreate}
        onClose={closeCreateModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Box
            sx={{
              p: 3,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Typography fontSize={26} fontWeight={900}>
              Create a company entity
            </Typography>

            <IconButton
              onClick={closeCreateModal}
              size="small"
              disabled={creating}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider />

          <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.25 }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Name"
                placeholder="Enter the entity name"
                value={createForm.name}
                onChange={handleCreateChange("name")}
              />
              <TextField
                fullWidth
                size="small"
                label="Initials"
                placeholder="Enter the entity initials"
                value={createForm.initials}
                onChange={handleCreateChange("initials")}
              />
            </Box>

            <TextField
              fullWidth
              size="small"
              label="Entity address"
              placeholder="Enter the entity address"
              value={createForm.entityAddress}
              onChange={handleCreateChange("entityAddress")}
            />

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Value"
                placeholder="Enter a value"
                value={createForm.value}
                onChange={handleCreateChange("value")}
              />

              <FormControl fullWidth size="small">
                <InputLabel>Server</InputLabel>
                <Select
                  label="Server"
                  value={createForm.server}
                  onChange={handleCreateChange("server")}
                >
                  <MenuItem value="">
                    <em>Select an option</em>
                  </MenuItem>
                  {SERVER_OPTIONS.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={centeredChipsSx}>
              {chips
                .filter((c) => visibleCreateChips.includes(c.key))
                .map((c) => (
                  <Chip
                    key={c.key}
                    size="small"
                    label={c.label}
                    onDelete={() => handleDeleteCreateChip(c.key)}
                    sx={chipSx}
                  />
                ))}
            </Box>

            {createError ? <Alert severity="error">{createError}</Alert> : null}

            <Box
              sx={{
                mt: 1,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Button
                variant="outlined"
                onClick={closeCreateModal}
                disabled={creating}
                sx={{ textTransform: "none", fontWeight: 900, borderRadius: 2 }}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                color="error"
                onClick={handleCreate}
                sx={{
                  textTransform: "none",
                  fontWeight: 900,
                  borderRadius: 2,
                  minWidth: 110,
                }}
                disabled={!canCreate || creating}
              >
                {creating ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Create"
                )}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* EDIT MODAL */}
      <Dialog
        open={openEdit}
        onClose={closeEditModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Box
            sx={{
              p: 3,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Typography fontSize={26} fontWeight={900}>
              Edit the company entity
            </Typography>

            <IconButton
              onClick={closeEditModal}
              size="small"
              disabled={updating}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider />

          <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.25 }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Name"
                value={editForm.name}
                onChange={handleEditChange("name")}
              />

              <TextField
                fullWidth
                size="small"
                label="Initials"
                value={editForm.initials}
                onChange={handleEditChange("initials")}
              />
            </Box>

            <TextField
              fullWidth
              size="small"
              label="Entity address"
              value={editForm.entityAddress}
              onChange={handleEditChange("entityAddress")}
            />

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Value"
                value={editForm.value}
                onChange={handleEditChange("value")}
              />

              <FormControl fullWidth size="small">
                <InputLabel>Server</InputLabel>
                <Select
                  label="Server"
                  value={editForm.server}
                  onChange={handleEditChange("server")}
                >
                  <MenuItem value="">
                    <em>Select an option</em>
                  </MenuItem>
                  {SERVER_OPTIONS.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={centeredChipsSx}>
              {chips
                .filter((c) => visibleEditChips.includes(c.key))
                .map((c) => (
                  <Chip
                    key={c.key}
                    size="small"
                    label={c.label}
                    onDelete={() => handleDeleteEditChip(c.key)}
                    sx={chipSx}
                  />
                ))}
            </Box>

            {updateError ? <Alert severity="error">{updateError}</Alert> : null}

            <Box
              sx={{
                mt: 1,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Button
                variant="outlined"
                onClick={closeEditModal}
                disabled={updating}
                sx={{ textTransform: "none", fontWeight: 900, borderRadius: 2 }}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                color="error"
                onClick={handleSave}
                sx={{
                  textTransform: "none",
                  fontWeight: 900,
                  borderRadius: 2,
                  minWidth: 110,
                }}
                disabled={!canSave || updating}
              >
                {updating ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Save"
                )}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* DELETE CONFIRM MODAL */}
      <Dialog
        open={openDeleteModal}
        onClose={closeDeleteModal}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 0,
            minWidth: { xs: "calc(100% - 32px)", sm: 460 },
          },
        }}
      >
        <DialogContent sx={{ p: 3 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 900, color: "#111827" }}>
            Delete entity
          </Typography>

          <Typography sx={{ mt: 1, fontSize: 14, color: "#6B7280" }}>
            Are you sure you want to delete{" "}
            <b>{selectedDeleteEntity?.name || "this entity"}</b>? This action cannot
            be undone.
          </Typography>

          {deleteError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          ) : null}

          <Box
            sx={{
              mt: 3,
              display: "flex",
              gap: 1.2,
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="outlined"
              onClick={closeDeleteModal}
              disabled={!!deletingId}
              sx={{
                textTransform: "none",
                fontWeight: 900,
                borderRadius: 2,
              }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              color="error"
              onClick={handleDelete}
              disabled={!!deletingId}
              sx={{
                textTransform: "none",
                fontWeight: 900,
                borderRadius: 2,
                minWidth: 110,
              }}
            >
              {deletingId ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Delete"
              )}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* SUCCESS MODAL */}
      <Dialog
        open={openSuccessModal}
        onClose={() => setOpenSuccessModal(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 0,
            minWidth: { xs: "calc(100% - 32px)", sm: 460 },
          },
        }}
      >
        <DialogContent sx={{ p: 3, textAlign: "center" }}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
            <CheckCircleIcon sx={{ fontSize: 54, color: "#16A34A" }} />
          </Box>

          <Typography sx={{ fontSize: 22, fontWeight: 900, color: "#111827" }}>
            {successTitle}
          </Typography>

          <Typography sx={{ mt: 1, fontSize: 14, color: "#6B7280" }}>
            {successDescription}
          </Typography>

          {successEntityId ? (
            <Typography sx={{ mt: 2, fontSize: 14, fontWeight: 800, color: "#111827" }}>
              Entity ID: #{successEntityId}
            </Typography>
          ) : null}

          <Box sx={{ mt: 3, display: "flex", gap: 1.2, justifyContent: "center" }}>
            <Button
              variant="contained"
              color="error"
              onClick={() => setOpenSuccessModal(false)}
              sx={{
                textTransform: "none",
                fontWeight: 900,
                borderRadius: 2,
              }}
            >
              Continue
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </PageRoot>
  );
}

export default CompanyEntities;
